import './i18n/config';

import { Suspense, lazy, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	BrowserRouter as Router,
	Switch,
	Route,
	Redirect,
} from "react-router-dom";

// WARNING!!! Must be above Admin (and in fact any other component) otherwise it overrides styles of components
// WARNING!!! Broke the Landing page because the same components were loaded in Home.tsx and styles.scss overrided the Landing page
// WARNING!!! https://stackoverflow.com/a/63308877
import './styles.scss';
import 'react-virtualized/styles.css';


// import Admin from './components/admin/admin';
// import Authentication from './components/admin/authentication/authentication';
import Home from './components/root/home';
import { getAdminChannels, refreshToken } from './store/actions/authentication';
import { EventsState } from './store/types';
import MonitorUserTime from './utils/session-timer';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Alerts from './components/general-ui/alert/alert';
import LiveEvent from './components/live-event/live-event';
// import ModeratorPageContainer from './components/moderation/containers/moderation-page/moderation-page.container';
import Uploader from './components/general-ui/uploader/uploader';
import WaitingIndicator from './components/general-ui/waiting-indicator/waiting-indicator';

const Admin = lazy(() => import('./components/admin/admin'));
const Authentication = lazy(() => import('./components/admin/authentication/authentication'));
const ModeratorPageContainer = lazy(() => import('./components/moderation/containers/moderation-page/moderation-page.container'));

const adminDomainRegex = /^admin\..*/; // Starts with 'admin.'

const isAdmin = adminDomainRegex.test(window.location.hostname) || window.location.hostname === 'localhost';

// keeping around until auth is totally sorted
// const requireLogin = process.env.REACT_APP_REQUIRE_LOGIN === 'true';

// const authRedirectURL = `https://${process.env.REACT_APP_BASE_URL?.replace('https://', 'admin.')}/authentication/sign-in?event=${window.location.href}`;

if (window.location.hostname.startsWith('www.brandlive')) {
	window.location.href = window.location.href.replace(/www/, 'admin');
}

const App: React.FC = () => {
	const { token } = useSelector((state: EventsState) => state.AuthReducer);
	const dispatch = useDispatch();

	useEffect(() => {
		//if the user is signed in on startup, refresh their token first thing to catch any changes
		if (token) {
			dispatch(refreshToken(token));
			dispatch(getAdminChannels(token));
		}

		//checks for user activity - if the user is signed in and has had no activity for the last 1 hour, sign them out. Will redirect them to the sign in form if they're on an admin route
		MonitorUserTime();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fallback = useCallback(() => (
		<div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
			<WaitingIndicator />
		</div>
	), []);

	return (
		<Suspense fallback="">
			<div className="App" style={{ pointerEvents: 'auto' }}>
				<Router>
					{
						isAdmin ? (
							<Switch>
								<Route path="/authentication">
									<Suspense fallback={fallback()}>
										<Authentication />
									</Suspense>
								</Route>
								<Route path="/event-deprecated/:uuid">
									{/** TODO: Remove this method when Windmill is finished */}
									<LiveEvent debug={true} />
								</Route>
								<Route path="/event/:uuid">
									{/** Will need (nginx?) routing method to forward hostname here - routing tbd, but this will work for now */}
									<LiveEvent debug={true} />
								</Route>
								{!token && // All routes after this require being logged in
									<Redirect
										to={{
											pathname: "/authentication/sign-in",
											state: { referrer: window.location.pathname }
										}}
									/>
								}
								<Route path="/admin">
									<Suspense fallback={fallback()}>
										<Admin />
									</Suspense>
								</Route>
								<Route path="/moderator/event/:event_uuid/:lang/session/:session_uuid">
									<Suspense fallback={fallback()}>
										<ModeratorPageContainer />
									</Suspense>
								</Route>
								<Route path="/">
									<Home />
								</Route>
							</Switch>
						) : (
							<Switch>
								<Route path="/">
									<LiveEvent host={window.location.hostname} eventNamePass={window.location.pathname.split("/")?.[1]} />
								</Route>
							</Switch>
						)
					}

				</Router>
			</div>

			<div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }} id="modal-global-container"></div>
			<div id="date-portal"></div>
			<Uploader />
			<Alerts />
		</Suspense>
	);
};

export default App;
